import { useEffect, useMemo, useState } from "react";
import { useRangeSelection } from "@/hooks/use-range-selection";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Sparkles, Briefcase, Wrench, Building2, AlertCircle, Info } from "lucide-react";
import { VENDOR_ROLES, MAINTENANCE_SPECIALTIES } from "@shared/schema";
import type { Vendor, Property } from "@shared/schema";

const ROLE_LABELS: Record<string, string> = {
  cleaning: "Cleaning",
  maintenance: "Maintenance",
  property_manager: "Property Manager",
  other: "Other",
};

const SPECIALTY_LABELS: Record<string, string> = {
  electrical: "Electrical",
  hvac: "HVAC",
  handyman: "Handyman",
  plumbing: "Plumbing",
  landscaping: "Landscaping",
  pest_control: "Pest Control",
  roof_repair: "Roof Repair",
  laundry_repair: "Laundry Repair",
  painting: "Painting",
  appliance_repair: "Appliance Repair",
  locksmith: "Locksmith",
  flooring: "Flooring",
  drywall: "Drywall",
  carpentry: "Carpentry",
  window_repair: "Window Repair",
  garage_door: "Garage Door",
};

interface VendorOnboardingDialogProps {
  // `isLinked` marks a roster-linked vendor (owned by another tenant): the
  // role/properties overlay is saved on the vendor_host_relationships row
  // instead of the vendor itself, and global vendor flags are hidden.
  vendor: (Vendor & { isLinked?: boolean }) | null;
  open: boolean;
  onClose: () => void;
}

export default function VendorOnboardingDialog({ vendor, open, onClose }: VendorOnboardingDialogProps) {
  const { toast } = useToast();
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<number[]>([]);
  const [propertySearch, setPropertySearch] = useState("");
  const [marketplaceBlocked, setMarketplaceBlocked] = useState(false);

  const propertiesQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
    enabled: open,
  });

  useEffect(() => {
    if (open && vendor) {
      setSelectedRoles(Array.isArray(vendor.roles) ? (vendor.roles as string[]) : []);
      setSelectedSpecialties(Array.isArray(vendor.serviceTypes) ? (vendor.serviceTypes as string[]) : []);
      setSelectedPropertyIds(Array.isArray(vendor.propertyIds) ? (vendor.propertyIds as number[]) : []);
      setPropertySearch("");
      setMarketplaceBlocked(vendor.isMarketplaceBlocked === true);
    }
  }, [open, vendor]);

  const isLinked = vendor?.isLinked === true;

  const updateMutation = useMutation({
    mutationFn: async (data: { roles: string[]; serviceTypes: string[]; propertyIds: number[]; isMarketplaceBlocked: boolean }) => {
      if (!vendor) throw new Error("No vendor");
      if (isLinked) {
        // Linked vendor: write this host's overlay on the relationship row.
        // isMarketplaceBlocked is a global vendor flag the linker must not set.
        const { roles, serviceTypes, propertyIds } = data;
        const res = await apiRequest("PATCH", `/api/app/operations/marketplace/${vendor.id}/relationship`, { roles, serviceTypes, propertyIds });
        return res.json();
      }
      const res = await apiRequest("PATCH", `/api/app/operations/vendors/${vendor.id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/vendors"] });
      if (vendor) queryClient.invalidateQueries({ queryKey: ["/api/app/operations/vendors", vendor.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/getting-started"] });
      toast({ title: "Vendor set up", description: "Roles, specialties, and properties saved." });
      onClose();
    },
    onError: (e: unknown) => {
      toast({
        title: "Error",
        description: parseErrorMessage(e, "Couldn't save vendor settings. Please try again."),
        variant: "destructive",
      });
    },
  });

  const LIVE_STATUSES = ["active", "launch", "renovations"];
  const properties = useMemo(
    () => (propertiesQuery.data || []).filter((p) => LIVE_STATUSES.includes(p.status)),
    [propertiesQuery.data],
  );
  const filteredProperties = propertySearch
    ? properties.filter((p) => String(p.name ?? "").toLowerCase().includes(propertySearch.toLowerCase()))
    : properties;
  const livePropertyIdSet = useMemo(() => new Set(properties.map((p) => p.id)), [properties]);
  const liveSelectedCount = selectedPropertyIds.filter((id) => livePropertyIdSet.has(id)).length;
  const allLiveSelected = properties.length > 0 && properties.every((p) => selectedPropertyIds.includes(p.id));

  const selectedPropertySet = useMemo(() => new Set(selectedPropertyIds), [selectedPropertyIds]);
  const { handleItemClick: handlePropertyClick } = useRangeSelection(
    filteredProperties.map((p) => p.id),
    selectedPropertySet,
    (next) => {
      const hiddenIds = selectedPropertyIds.filter((id) => !livePropertyIdSet.has(id));
      const liveNext = Array.from(next).filter((id) => livePropertyIdSet.has(id));
      setSelectedPropertyIds([...hiddenIds, ...liveNext]);
    },
  );

  const toggleRole = (role: string) => {
    setSelectedRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  };
  const toggleSpecialty = (spec: string) => {
    setSelectedSpecialties((prev) => (prev.includes(spec) ? prev.filter((s) => s !== spec) : [...prev, spec]));
  };
  const toggleProperty = (id: number) => {
    setSelectedPropertyIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleSelectAllProperties = () => {
    const hiddenIds = selectedPropertyIds.filter((id) => !livePropertyIdSet.has(id));
    if (allLiveSelected) {
      setSelectedPropertyIds(hiddenIds);
    } else {
      setSelectedPropertyIds([...hiddenIds, ...properties.map((p) => p.id)]);
    }
  };

  const showsSpecialtiesSection = selectedRoles.includes("maintenance");
  const hasNothingSelected =
    selectedRoles.length === 0 && selectedSpecialties.length === 0 && selectedPropertyIds.length === 0;

  const handleSave = () => {
    updateMutation.mutate({
      roles: selectedRoles,
      serviceTypes: showsSpecialtiesSection ? selectedSpecialties : [],
      propertyIds: selectedPropertyIds,
      isMarketplaceBlocked: marketplaceBlocked,
    });
  };

  if (!vendor) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !updateMutation.isPending) onClose(); }}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] flex flex-col"
        data-testid="dialog-vendor-onboarding"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-brand" />
            Set up {vendor.firstName || String(vendor.name ?? "").split(" ")[0]}
          </DialogTitle>
          <DialogDescription>
            Pick the vendor's roles, maintenance specialties, and which properties they work at. This makes sure
            they actually see properties and tasks the moment you invite them.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 py-2">
          {/* Roles */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Briefcase className="h-4 w-4 text-gray-500" />
              <h3 className="font-semibold text-sm text-gray-900">Roles</h3>
              <span className="text-xs text-gray-500">What kind of work do they do?</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {VENDOR_ROLES.map((role) => (
                <label
                  key={role}
                  className="flex items-center gap-2 cursor-pointer p-2 rounded border hover:bg-gray-50 dark:hover:bg-gray-800"
                  data-testid={`onboarding-checkbox-role-${role}`}
                >
                  <Checkbox
                    checked={selectedRoles.includes(role)}
                    onCheckedChange={() => toggleRole(role)}
                  />
                  <span className="text-sm text-gray-800 dark:text-gray-200">
                    {ROLE_LABELS[role] || role}
                  </span>
                </label>
              ))}
            </div>
          </section>

          {showsSpecialtiesSection && (
            <>
              <Separator />
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Wrench className="h-4 w-4 text-gray-500" />
                  <h3 className="font-semibold text-sm text-gray-900">Maintenance specialties</h3>
                  <span className="text-xs text-gray-500">Pick all that apply.</span>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {MAINTENANCE_SPECIALTIES.map((spec) => (
                    <label
                      key={spec}
                      className="flex items-center gap-2 cursor-pointer p-2 rounded border hover:bg-gray-50 dark:hover:bg-gray-800"
                      data-testid={`onboarding-checkbox-specialty-${spec}`}
                    >
                      <Checkbox
                        checked={selectedSpecialties.includes(spec)}
                        onCheckedChange={() => toggleSpecialty(spec)}
                      />
                      <span className="text-sm text-gray-800 dark:text-gray-200">
                        {SPECIALTY_LABELS[spec] || spec}
                      </span>
                    </label>
                  ))}
                </div>
              </section>
            </>
          )}

          <Separator />

          <section>
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="h-4 w-4 text-gray-500" />
              <h3 className="font-semibold text-sm text-gray-900">Preferred properties</h3>
              <span className="text-xs text-gray-500">{liveSelectedCount} selected</span>
            </div>
            <div className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-3">
                <label
                  className="flex items-center gap-2 cursor-pointer"
                  data-testid="onboarding-checkbox-properties-all"
                >
                  <Checkbox checked={allLiveSelected} onCheckedChange={toggleSelectAllProperties} />
                  <span className="text-sm font-medium">Select all properties</span>
                </label>
              </div>
              <Input
                placeholder="Search..."
                value={propertySearch}
                onChange={(e) => setPropertySearch(e.target.value)}
                className="mb-3"
                data-testid="onboarding-input-property-search"
              />
              <div className="space-y-1 max-h-[220px] overflow-y-auto">
                {propertiesQuery.isLoading && (
                  <p className="text-sm text-gray-400 py-2">Loading properties…</p>
                )}
                {!propertiesQuery.isLoading && filteredProperties.length === 0 && (
                  <p className="text-sm text-gray-400 py-2">
                    {properties.length === 0
                      ? "You don't have any active properties yet."
                      : "No properties match your search."}
                  </p>
                )}
                {filteredProperties.map((property) => (
                  <div
                    key={property.id}
                    className="flex items-center gap-2 cursor-pointer py-1 px-1 rounded hover:bg-gray-50 dark:hover:bg-gray-800"
                    data-testid={`onboarding-checkbox-property-${property.id}`}
                    onClick={(e) => { e.stopPropagation(); handlePropertyClick(property.id, e.shiftKey); }}
                  >
                    <Checkbox
                      checked={selectedPropertyIds.includes(property.id)}
                      onCheckedChange={() => {}}
                    />
                    <div className="w-7 h-7 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs text-gray-500 dark:text-gray-300">
                      {property.name[0]}
                    </div>
                    <span className="text-sm text-gray-800 dark:text-gray-200 truncate">{property.name}</span>
                  </div>
                ))}
              </div>
            </div>
            {hasNothingSelected && (
              <div className="mt-3 flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded p-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  Heads up: if you invite this vendor without picking any roles, specialties, or properties,
                  they'll log in to an empty portal and won't know what to do.
                </span>
              </div>
            )}
          </section>

          {!isLinked && <Separator />}

          {!isLinked && <section>
            <div className="flex items-start gap-2">
              <Checkbox
                id="onboarding-marketplace-blocked"
                checked={marketplaceBlocked}
                onCheckedChange={(checked) => setMarketplaceBlocked(checked === true)}
                className="mt-0.5"
                data-testid="onboarding-checkbox-marketplace-blocked"
              />
              <label
                htmlFor="onboarding-marketplace-blocked"
                className="flex items-center gap-1.5 text-sm font-normal leading-snug cursor-pointer text-gray-800 dark:text-gray-200"
              >
                Block vendor from being shown in the marketplace
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3.5 w-3.5 text-gray-400 shrink-0" data-testid="onboarding-tooltip-marketplace-blocked" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      Vendor will only be able to be added by someone directly who already has their phone number.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </label>
            </div>
          </section>}
        </div>

        <DialogFooter className="flex flex-col sm:flex-row sm:justify-between gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            data-testid="button-onboarding-skip"
          >
            Skip for now
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={updateMutation.isPending}
            data-testid="button-onboarding-save"
          >
            {updateMutation.isPending ? "Saving…" : "Save and continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
